import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useLocation, useNavigate } from 'react-router-dom'
import { Clock, LogOut, RefreshCw, AlertTriangle } from 'lucide-react'
import { authApi } from '@/lib/api/auth'
import toast from 'react-hot-toast'

interface SessionManagerProps {
  children: React.ReactNode
}

export function SessionManager({ children }: SessionManagerProps) {
  const { user, logout, refreshToken } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [showWarning, setShowWarning] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isExtending, setIsExtending] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastPathRef = useRef(location.pathname)

  // Store the last accessed path
  useEffect(() => {
    if (user && location.pathname !== '/login') {
      lastPathRef.current = location.pathname
      localStorage.setItem('lastAccessedPath', location.pathname)
    }
  }, [location.pathname, user])

  // Get token expiry from server
  const getTokenExpiry = useCallback(async () => {
    try {
      const response = await authApi.verifyToken()
      if (response.session?.expires_at) {
        return response.session.expires_at * 1000 // Convert to milliseconds
      }
      return null
    } catch (error) {
      return null
    }
  }, [])

  // Handle logout with reason
  const handleLogout = useCallback(async (reason: string, showToast: boolean = true) => {
    // Clear intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }

    // Hide the session warning popup immediately
    setShowWarning(false)
    setTimeRemaining(null)
    setIsExtending(false)

    // Store the current path for return
    const currentPath = location.pathname
    if (currentPath !== '/login') {
      localStorage.setItem('returnPath', currentPath)
    }

    // Logout - this will show its own toast
    await logout()
    
    // Only show additional toast if requested (for session expiry)
    if (showToast && reason !== 'User logged out') {
      toast.error(reason)
    }
    
    navigate('/login', { 
      replace: true, 
      state: { from: { pathname: currentPath } } 
    })
  }, [location.pathname, logout, navigate])

  // Check session expiry
  const checkSessionExpiry = useCallback(async () => {
    if (showWarning) return // Don't check again if warning is already shown
    
    const expiry = await getTokenExpiry()
    if (!expiry) return

    const now = Date.now()
    const timeLeft = expiry - now
    const fiveMinutes = 5 * 60 * 1000

    if (timeLeft <= 0) {
      // Token expired
      handleLogout('Session expired')
    } else if (timeLeft <= fiveMinutes) {
      // Show warning when 5 minutes or less remaining
      setShowWarning(true)
      const secondsLeft = Math.floor(timeLeft / 1000)
      setTimeRemaining(secondsLeft)
    }
  }, [showWarning, getTokenExpiry, handleLogout])

  // Countdown timer effect
  useEffect(() => {
    if (showWarning && timeRemaining !== null && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => {
          if (prev === null || prev <= 1) {
            handleLogout('Session expired', true)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [showWarning, timeRemaining, handleLogout])

  // Extend session
  const handleExtendSession = async () => {
    setIsExtending(true)
    try {
      await refreshToken()
      
      // Clear warning state
      setShowWarning(false)
      setTimeRemaining(null)
      
      toast.success('Session extended successfully')
      
      // Reset the check cycle after a brief delay
      setTimeout(() => {
        checkSessionExpiry()
      }, 2000)
    } catch (error: any) {
      console.error('Failed to extend session:', error)
      // Don't automatically logout on extension failure
      toast.error('Failed to extend session. Please try again.')
    } finally {
      setIsExtending(false)
    }
  }

  // Set up session monitoring
  useEffect(() => {
    if (!user) return

    // Check immediately
    checkSessionExpiry()

    // Check every 30 seconds
    const checkInterval = setInterval(checkSessionExpiry, 30000)

    return () => {
      clearInterval(checkInterval)
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
    }
  }, [user, checkSessionExpiry])

  // Format time for display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Check for permission changes (403 errors are handled by API interceptor)
  useEffect(() => {
    const handlePermissionError = () => {
      toast.error('Access denied: Your permissions have changed')
      navigate('/dashboard')
    }

    window.addEventListener('permission-error' as any, handlePermissionError as any)
    return () => {
      window.removeEventListener('permission-error' as any, handlePermissionError as any)
    }
  }, [navigate])

  return (
    <>
      {children}
      
      {/* Session Warning Modal */}
      {showWarning && timeRemaining !== null && timeRemaining > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#000742] rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-[#5869ff]/20">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-orange-100 dark:bg-orange-500/10 rounded-full">
                <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Session Expiring Soon
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your session will expire in:
                </p>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="flex items-center justify-center my-8">
              <div className="relative">
                <div className="text-5xl font-mono font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                  {formatTime(timeRemaining)}
                </div>
                <div className="absolute -top-3 -right-3">
                  <Clock className="w-5 h-5 text-orange-500 animate-pulse" />
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center">
              Would you like to extend your session?
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => handleLogout('User logged out', false)}
                disabled={isExtending}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-all duration-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
              <button
                onClick={handleExtendSession}
                disabled={isExtending}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
              >
                {isExtending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Extending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Extend Session
                  </>
                )}
              </button>
            </div>

            {/* Warning Message */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
              If no action is taken, you will be automatically logged out when the timer reaches zero.
            </p>
          </div>
        </div>
      )}
    </>
  )
}